import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Card, Col, Flex, Form, Input, message, Row, Select, Typography } from "antd";
import { useForm } from "antd/es/form/Form";
import { disturbanceInformationService } from "../../services/disturbance-information.service";
import { useNavigate, useParams } from "react-router";
import { useEffect, useState } from "react";
import { format, formatDuration, intervalToDuration, parseISO } from "date-fns";
import { districts } from "../../utils/options.data";
import ImageUploader from "../../components/ImageUpload";
import { API_BASE_URL } from "../../config/api.config";

export default function DisturbanceInformationForm(){
  const { Text } = Typography;
  const { id } = useParams();
  const [form] = useForm();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [imageUrl, setImageUrl] = useState(null);

  const { data: disturbance  } = useQuery({
    queryKey: ['detailDisturbance', id],
    queryFn: () => disturbanceInformationService.getById(id),
    enabled: !!id
  })

  useEffect(() => {
    if(id && disturbance?.data){
      form.setFieldValue('id', disturbance?.data?.id)
      form.setFieldValue('title', disturbance?.data?.title)
      form.setFieldValue('description', disturbance?.data?.description)
      form.setFieldValue('location', disturbance?.data?.location)
      form.setFieldValue('district', disturbance?.data?.district)
      form.setFieldValue('start_date', format(new Date(disturbance?.data?.start_date), 'yyyy-MM-dd'))
      form.setFieldValue('end_date', format(new Date(disturbance?.data?.end_date), 'yyyy-MM-dd'))
      form.setFieldValue('start_time', disturbance?.data?.start_time)
      form.setFieldValue('end_time', disturbance?.data?.end_time)
      form.setFieldValue('disturbance_date', format(new Date(disturbance?.data?.disturbance_date), 'yyyy-MM-dd'))
      form.setFieldValue('recovery_time', disturbance?.data?.recovery_time)
    }
  
  }, [disturbance, form, id])

  useEffect(() => {
    if (disturbance?.data?.image) {
      setImageUrl(`${API_BASE_URL}/${disturbance.data.image}`);
    }
  }, [disturbance]);

  const startDate = Form.useWatch('start_date', form)
  const startTime= Form.useWatch('start_time', form)
  const endDate = Form.useWatch('end_date', form)
  const endTime= Form.useWatch('end_time', form)

  useEffect(() => {
    if(startDate && startTime && endDate && endTime){
      const startDateTime = parseISO(`${startDate}T${startTime}`);
      const endDateTime = parseISO(`${endDate}T${endTime}`);
      const duration = intervalToDuration({ start: startDateTime, end: endDateTime });
      const diff = formatDuration(duration);
      form.setFieldValue('recovery_time', diff)
    }
  
  }, [startDate, startTime, endDate, endTime, form])

  const storeMutation = useMutation({
    mutationFn: (data) => disturbanceInformationService.store(data),
    onSuccess: () => {
      queryClient.invalidateQueries('users');
      message.success('Data berhasil disimpan');
      form.resetFields();
      navigate('/disturbance-information')
    },
    onError: (error) => {
      console.log(error)
      message.error('Terjadi Kesalahan');
    }
  })

  const updateMutation = useMutation({
    mutationFn: (data) => disturbanceInformationService.update(data),
    onSuccess: () => {
      queryClient.invalidateQueries('users');
      message.success('Data berhasil diubah');
      form.resetFields();
      navigate('/disturbance-information')
    },
    onError: (error) => {
      console.log(error)
      message.error('Terjadi Kesalahan');
    }
  })

  const handleSubmit = (values) => {
    // Get the image file from the form
    const imageFile = form.getFieldValue('image');
    
    if(!id) {
      // For new record, image is required
      const data = values
      if(imageFile) {
        data.image = imageFile
      }
      storeMutation.mutate(data);
    } else {
      let data = { ...values };
      
      // For update: if there's a new image file, include it
      if(imageFile instanceof File) {
        data.image = imageFile;
      }
      
      updateMutation.mutate(data);
    }
  }

  return (
    <>
      <Card
        title={ (id ? "Ubah" : "Tambah") + " Data"}
        actions={[
          <Flex key={1} justify="end" style={{ paddingRight: 24 }} gap={16} >
            <Button onClick={() => navigate('/disturbance-information')} variant="outlined" color="primary" >
              Batal
            </Button>
            <Button 
              variant="solid" 
              color="primary" 
              onClick={() => form.submit()}
            >
              Simpan
            </Button>
          </Flex>
        ]}
      >
        <Form
          layout="vertical"
          form={form}
          onFinish={handleSubmit}
        >
          <Form.Item hidden name={'id'} >
            <Input/>
          </Form.Item>
          <Form.Item
            name={'title'}
            label="Judul"
            rules={[
              {
                required: true
              }
            ]}
          >
            <Input/>
          </Form.Item>
          
          {/* Image upload field added below title */}
          <Col xs={24} md={12} style={{ marginTop: 15, marginBottom: 15 }}>
            <ImageUploader
              form={form}
              name="image"
              label=""
              imageFromAPI={imageUrl}
            />
            <Text
              type="secondary"
              style={{
                fontSize: "12px",
                display: "block",
                wordBreak: "break-word",
              }}
            >
              Max file size 2MB, tipe file .jpg, .png, & .webp, Ukuran 389 × 389
            </Text>
          </Col>
          
          <Form.Item
            name={'description'}
            label="Deskripsi"
            rules={[
              {
                required: true
              }
            ]}
          >
            <Input.TextArea/>
          </Form.Item>
          <Col xs={24} md={12} style={{ marginTop: 15, marginBottom: 15 }}>
          <Form.Item
          style={{ marginBottom: 5 }}
            name={'location'}
            label="Lokasi"
            rules={[
              {
                required: true
              }
            ]}
          >
            <Input/>
          </Form.Item>
            <Text
              type="secondary"
              style={{
                fontSize: "12px",
                display: "block",
                wordBreak: "break-word",
              }}
            >
              Note : Input Nama Jalan
            </Text>
          </Col>
          
          <Row gutter={[20]} >
            <Col span={12} >
              <Form.Item
                name={'disturbance_date'}
                label="Tanggal Gangguan"
                rules={[
                  {
                    required: true
                  }
                ]}
              >
                <Input type="date" />
              </Form.Item>
            </Col>
            <Col span={12} >
              <Form.Item
                name={'district'}
                label="Kelurahan"
                rules={[
                  {
                    required: true
                  }
                ]}
              >
                <Select
                  options={districts}
                  mode="tags"
                  filterOption
                />
              </Form.Item>
            </Col>
            <Col span={7} >
              <Form.Item
                name={'start_date'}
                label="Tanggal Awal Pengerjaan"
                rules={[
                  {
                    required: true
                  }
                ]}
              >
                <Input type="date" />
              </Form.Item>
            </Col>
            <Col span={5} >
              <Form.Item
                name={'start_time'}
                label="Waktu Awal Pengerjaan"
                rules={[
                  {
                    required: true
                  }
                ]}
              >
                <Input type="time" />
              </Form.Item>
            </Col>
            <Col span={7} >
              <Form.Item
                name={'end_date'}
                label="Tanggal Akhir Pengerjaan"
                rules={[
                  {
                    required: true
                  }
                ]}
              >
                <Input type="date" />
              </Form.Item>
            </Col>
            <Col span={5} >
              <Form.Item
                name={'end_time'}
                label="Waktu Akhir Pengerjaan"
                rules={[
                  {
                    required: true
                  }
                ]}
              >
                <Input type="time" />
              </Form.Item>
            </Col>
            <Col span={24} >
              <Form.Item
                name={'recovery_time'}
                label="Durasi Pekerjaan"
                rules={[
                  {
                    required: true
                  }
                ]}
              >
                <Input readOnly />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Card>
    </>
  )
}